import { useState, ChangeEvent, useEffect, useContext } from 'react';

import { TextField, FormControl } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { CustomFieldExtensionSchema } from '@backstage/plugin-scaffolder-react';
import useDebounce from 'react-use/lib/useDebounce';
import { UserContext } from '../../contexts/UserContext';
import { ConfigHelper } from '@devxdiscover/devhub-common';
import axios from 'axios';
import { Group as MsGraphGroup } from '@microsoft/microsoft-graph-types';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { UIOptionsType } from '@rjsf/utils';

/**
 * The schema for the {@link AzureActiveDirectoryGroupPicker} properties.
 */
export const AzureActiveDirectoryGroupPickerSchema: CustomFieldExtensionSchema =
  {
    returnValue: {
      type: 'string',
    },
  };

// use enum - refactor this code
export type GroupTypeFilter = UIOptionsType & {
  types: string[];
};
/**
 * Gets the groups for the user from the backend.
 * @param url - The URL to fetch the groups from.
 * @param options - The options to pass to the fetch function.
 */
const useGetUserGroups = (
  url: string,
  options?: {
    login: string;
    groupType?: string[];
    groupName?: string;
  },
) => {
  const [data, setData] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await axios.post<MsGraphGroup[]>(url, {
          login: options?.login,
          grpName: options?.groupName,
          grpTypes: {
            types: options?.groupType,
          },
        });

        if (response.status !== 200) {
          throw new Error(`Error: ${response.statusText}`);
        }
        const result = await response.data;
        if (!result || !Array.isArray(result)) {
          throw new Error('Invalid response data');
        }
        // Map the result to the expected format

        const groupNames = result
          .map(group => {
            // Check if the group object is not null or undefined
            return group && group?.displayName ? group.displayName : '';
          })
          .filter(group => group !== '');
        // Filter out any undefined or null values
        if (groupNames && groupNames.length) {
          setData(groupNames);
        }
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options?.groupName]);

  return { data, loading, error };
};

export type AzureActiveDirectoryGroupState = FieldExtensionComponentProps<
  string | undefined,
  GroupTypeFilter
>;
/**
 * The input props that can be specified under `ui:options` for the
 * `ActiveDirectoryGroupPicker` field extension.
 * @param props - The properties passed to the component.
 * @public
 */
export const AzureActiveDirectoryGroupPicker = (
  props: FieldExtensionComponentProps<string | undefined, GroupTypeFilter>,
) => {
  const { formData, onChange, rawErrors, idSchema, required, uiSchema } = props;

  const [groups, setGroups] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState<string>('');
  const [grpName, setGrpName] = useState<string | undefined>(undefined);
  const { login } = useContext(UserContext);

  const customOptions = uiSchema['ui:options'] as GroupTypeFilter;
  const { data, loading, error } = useGetUserGroups(
    ConfigHelper.instance.apiUrl('msgraph/user/memberOf').href,
    {
      login: login,
      groupName: grpName,

      groupType: customOptions?.types,
    },
  );
  useEffect(
    () => {
      if (!loading && data) {
        setGroups(data);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, loading, error],
  );

  useDebounce(
    () => {
      setGrpName(inputValue);
    },
    500,
    [inputValue],
  );

  // Event Handler for the text box and to set the value
  const onHandleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setInputValue(value);
  };

  const onSelect = (_: any, value: string | null) => {
    if (!value) {
      return;
    }
    onChange(value);
  };

  return (
    <FormControl
      margin="normal"
      required={required}
      error={rawErrors?.length > 0 && !formData}
    >
      {error ? (
        <TextField
          error
          label="Error"
          defaultValue="No Groups Available"
          helperText="Failed to fetch group details."
          variant="outlined"
        />
      ) : (
        <Autocomplete
          id={idSchema?.$id}
          value={formData}
          loading={loading}
          loadingText="Loading.."
          options={groups}
          getOptionLabel={option => option}
          onChange={onSelect}
          autoSelect
          renderInput={params => (
            <TextField
              {...params}
              label="Groups available"
              margin="normal"
              helperText="Groups associated with user"
              variant="outlined"
              required
              onChange={onHandleChange}
              InputProps={params.InputProps}
            />
          )}
        />
      )}
    </FormControl>
  );
};
